import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Typography,
    Paper,
    Grid
} from '@mui/material';
import { Search as SearchIcon, Sync as SyncIcon } from '@mui/icons-material';
import apiClient from '../services/apiClient';

interface ExemptionSearchResult {
    recup_id: string;
    code: string;
    name: string;
    regional_code: string;
    category_name?: string;
    start_date?: string;
    end_date?: string;
}

interface ExemptionSearchModalProps {
    open: boolean;
    onClose: () => void;
    onSyncComplete: () => void;
}

const ExemptionSearchModal: React.FC<ExemptionSearchModalProps> = ({
    open,
    onClose,
    onSyncComplete
}) => {
    const [exemptionCode, setExemptionCode] = useState('');
    const [description, setDescription] = useState('');
    const [searching, setSearching] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [searchResults, setSearchResults] = useState<ExemptionSearchResult[]>([]);
    const [selectedExemptions, setSelectedExemptions] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);
    const [searchPerformed, setSearchPerformed] = useState(false);

    const handleSearch = async () => {
        if (!exemptionCode.trim() && !description.trim()) {
            setError('Inserire almeno un codice esenzione o una descrizione per la ricerca');
            return;
        }

        setSearching(true);
        setError(null);
        setSearchResults([]);
        setSelectedExemptions(new Set());
        setSearchPerformed(false);

        try {
            const params = new URLSearchParams();
            if (exemptionCode.trim()) params.append('exemptionCode', exemptionCode.trim());
            if (description.trim()) params.append('description', description.trim());

            const response = await apiClient.get(`/recup-sync/exemptions/search?${params}`);

            if (!response.data.found) {
                setError(response.data.message || 'Nessuna esenzione trovata');
                setSearchPerformed(true);
                return;
            }

            if (response.data.operation === 'single_sync') {
                // Sincronizzazione singola completata automaticamente
                alert(`Esenzione sincronizzata con successo: ${response.data.data.code}`);
                onSyncComplete();
                handleClose();
                return;
            }

            if (response.data.operation === 'already_exists') {
                // Esenzione già presente nel database locale
                alert(`Esenzione già presente nel database: ${response.data.data.code}`);
                onSyncComplete(); // Refresh per mostrare eventuali aggiornamenti
                handleClose();
                return;
            }

            if (response.data.operation === 'multiple_found') {
                // Multiple esenzioni trovate - mostra per selezione
                setSearchResults(response.data.data);
                setSearchPerformed(true);
                return;
            }

        } catch (err: any) {
            console.error('Search error:', err);
            setError(err.response?.data?.error || 'Errore durante la ricerca');
        } finally {
            setSearching(false);
        }
    };

    const handleExemptionToggle = (recupId: string) => {
        const newSelected = new Set(selectedExemptions);
        if (newSelected.has(recupId)) {
            newSelected.delete(recupId);
        } else {
            newSelected.add(recupId);
        }
        setSelectedExemptions(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedExemptions.size === searchResults.length) {
            // Deseleziona tutto
            setSelectedExemptions(new Set());
        } else {
            // Seleziona tutto
            setSelectedExemptions(new Set(searchResults.map(e => e.recup_id)));
        }
    };

    const handleSyncSelected = async () => {
        if (selectedExemptions.size === 0) {
            setError('Selezionare almeno un\'esenzione da sincronizzare');
            return;
        }

        setSyncing(true);
        setError(null);

        try {
            const exemptionsToSync = searchResults.filter(e => 
                selectedExemptions.has(e.recup_id)
            );

            const response = await apiClient.post('/recup-sync/exemptions/sync-selected', {
                exemptions: exemptionsToSync
            });

            if (response.data.success) {
                const stats = response.data.stats;
                alert(
                    `Sincronizzazione completata!\n` +
                    `Totale: ${stats.total}\n` +
                    `Successo: ${stats.successful}\n` +
                    `Errori: ${stats.errors}`
                );
                onSyncComplete();
                handleClose();
            } else {
                setError(response.data.error || 'Errore durante la sincronizzazione');
            }

        } catch (err: any) {
            console.error('Sync error:', err);
            setError(err.response?.data?.error || 'Errore durante la sincronizzazione');
        } finally {
            setSyncing(false);
        }
    };

    const handleClose = () => {
        setExemptionCode('');
        setDescription('');
        setSearchResults([]);
        setSelectedExemptions(new Set());
        setError(null);
        setSearchPerformed(false);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                Cerca e Sincronizza Esenzioni da RECUP
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Inserisci il codice esenzione o una descrizione per cercare nel sistema RECUP
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Codice Esenzione"
                                placeholder="es. E01, E02, ..."
                                value={exemptionCode}
                                onChange={(e) => setExemptionCode(e.target.value)}
                                disabled={searching || syncing}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Descrizione"
                                placeholder="es. diabete, invalidità, ..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                disabled={searching || syncing}
                            />
                        </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={searching || syncing}
                            startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                        >
                            {searching ? 'Ricerca in corso...' : 'Cerca'}
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {searchPerformed && searchResults.length === 0 && !error && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Nessuna esenzione trovata per i criteri specificati
                    </Alert>
                )}

                {searchResults.length > 0 && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                Esenzioni trovate ({searchResults.length})
                            </Typography>
                            <Button
                                size="small"
                                onClick={handleSelectAll}
                                disabled={syncing}
                            >
                                {selectedExemptions.size === searchResults.length ? 'Deseleziona tutto' : 'Seleziona tutto'}
                            </Button>
                        </Box>

                        <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <TableContainer>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">Sel.</TableCell>
                                            <TableCell>Codice</TableCell>
                                            <TableCell>Nome</TableCell>
                                            <TableCell>Codice Regionale</TableCell>
                                            <TableCell>Categoria</TableCell>
                                            <TableCell>Validità</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {searchResults.map((exemption) => (
                                            <TableRow key={exemption.recup_id}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedExemptions.has(exemption.recup_id)}
                                                        onChange={() => handleExemptionToggle(exemption.recup_id)}
                                                        disabled={syncing}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {exemption.code}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {exemption.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {exemption.regional_code || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {exemption.category_name || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {exemption.start_date && (
                                                            <>
                                                                Da: {new Date(exemption.start_date).toLocaleDateString()}
                                                                {exemption.end_date && (
                                                                    <br />
                                                                )}
                                                            </>
                                                        )}
                                                        {exemption.end_date && (
                                                            <>A: {new Date(exemption.end_date).toLocaleDateString()}</>
                                                        )}
                                                        {!exemption.start_date && !exemption.end_date && '-'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button 
                    onClick={handleClose} 
                    disabled={searching || syncing}
                >
                    Annulla
                </Button>
                
                {searchResults.length > 0 && (
                    <Button
                        variant="contained"
                        onClick={handleSyncSelected}
                        disabled={searching || syncing || selectedExemptions.size === 0}
                        startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                        color="primary"
                    >
                        {syncing 
                            ? 'Sincronizzazione...' 
                            : `Sincronizza Selezionate (${selectedExemptions.size})`
                        }
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ExemptionSearchModal;